import React, { memo, useMemo } from "react";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { BillingCycleType } from "../types";
import {
  BillingCycle,
  DiscountValue,
  DiscountValueYearlyPrice,
} from "../constants";
import PurchaseActions from "./PurchaseActions/index";
import FeatureStatic from "./FeatureStatic";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Member/Subscription/SubscriptionCard/SubscriptionCard";
import {
  Member,
  MemberProduct,
  MemberProductLevelEnum,
} from "@features/api/memberApi";
import { AnimateNumber } from "@components/motions/AnimateNumber";
import { Countdown } from "@components/CountDown/Countdown";
import { CountDownTime } from "@/src/hooks/usePlusCountDown";
import { MemberPlanInfo } from "@/src/hooks/useMemberUser";
import { getDisplayPlanName } from "../../shared/planDisplayUtils";
import {
  resolveCompleteDiscountPercent,
  resolveCompleteStrikethroughPrice,
} from "../planPricingUtils";
import { ProductStatus } from "../../CurrentPlan/constants";

const I18n = I18nUtil.createScoped(Translations);

interface SubscriptionCardProps {
  plan: MemberProduct & { afterPrice?: number };
  billingCycle: BillingCycleType;
  onStripeClick?: (planId: string) => void;
  onPaypalClick?: (planId: string) => void;
  isLoading?: boolean;
  userPlanInfo: Member & MemberPlanInfo;
  isTrial?: boolean;
  trialList?: MemberProduct[];
  hasActiveDiscount?: boolean;
  isDiscount?: boolean;
  time?: CountDownTime;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  plan,
  billingCycle,
  onStripeClick,
  onPaypalClick,
  isLoading = false,
  userPlanInfo,
  isTrial,
  trialList,
  hasActiveDiscount,
  isDiscount,
  time,
}) => {
  const isFree = plan?.level === MemberProductLevelEnum.FREE;
  const isRecommended = !isFree;
  const isActivity = isDiscount;
  const price = Number(plan?.price);

  const priceMeta = useMemo(() => {
    if (isFree) {
      return {
        displayPrice: 0,
        unit: I18n.t("/ yr"),
        chargeNote: I18n.t("Charged $0.00 every year"),
        originalPrice: undefined as number | undefined,
        discountPercent: 0,
      };
    }

    const isYearly = billingCycle === BillingCycle.YEAR;
    const isMonthly = billingCycle === BillingCycle.MONTH;
    const isWeekly = billingCycle === BillingCycle.WEEK;
    const isLifetime = billingCycle === BillingCycle.LIFETIME;

    if (isActivity) {
      const yearlyDiscountPrice =
        Number(DiscountValueYearlyPrice) * (1 - DiscountValue / 100);
      const monthlyDiscountPrice = yearlyDiscountPrice / 12;
      return {
        displayPrice: isYearly
          ? yearlyDiscountPrice
          : isMonthly
            ? monthlyDiscountPrice
            : price,
        unit: isYearly
          ? I18n.t("/ yr")
          : isMonthly
            ? I18n.t("/mo")
            : isWeekly
              ? I18n.t("/wk")
              : "",
        chargeNote: isYearly
          ? I18n.t("Charged %{var0} every month", {
              var0: `$${monthlyDiscountPrice.toFixed(2)}`,
            })
          : undefined,
        originalPrice: isYearly
          ? Number(DiscountValueYearlyPrice)
          : Number(DiscountValueYearlyPrice) / 12,
        discountPercent: DiscountValue,
      };
    }

    const monthlyEquivalent = isYearly ? price / 12 : price;
    const referenceMonthly = isYearly ? price / 12 : price;
    const referenceOriginal = resolveCompleteStrikethroughPrice(
      price,
      billingCycle,
    );
    const discountPercent = resolveCompleteDiscountPercent(
      price,
      referenceOriginal,
    );

    return {
      displayPrice: price,
      unit: isYearly
        ? I18n.t("/ yr")
        : isMonthly
          ? I18n.t("/mo")
          : isWeekly
            ? I18n.t("/wk")
            : "",
      chargeNote: isYearly ? (
        I18n.t("Charged %{var0} every month", {
          var0: `$${monthlyEquivalent.toFixed(2)}`,
        })
      ) : isMonthly ? (
        I18n.t("Charged %{var0} every month", {
          var0: `$${referenceMonthly.toFixed(2)}`,
        })
      ) : isLifetime ? (
        <span className="text-content_accent4_primary">
          {I18n.t("Limited to the first 2,000 members.")}
        </span>
      ) : undefined,
      originalPrice: referenceOriginal,
      discountPercent,
    };
  }, [billingCycle, isActivity, isFree, price]);

  // const userPayCycle = userPlanInfo?.payCycle;
  // const isCurrentPaidPlan =
  //   !isFree && (userPlanInfo?.isPro || userPlanInfo?.isExpert);
  // const currentPayCycle = userPayCycle === plan?.pay_cycle;
  // const showLabel =
  //   !isFree &&
  //   isCurrentPaidPlan &&
  //   currentPayCycle &&
  //   !userPlanInfo?.auto_renewal;
  const isLifetimeRecommended =
    billingCycle === BillingCycle.LIFETIME && isRecommended;

  const getPlanNameDescription = () => {
    if (isFree) {
      return I18n.t(
        "Students, casual observers, and new users testing the waters",
      );
    }
    if (isLifetimeRecommended) {
      return I18n.t(
        "Own lifetime access to Ed Complete. Locked-in pricing forever.",
      );
    }
    return I18n.t("One Ed. All your important money decisions, prepared.");
  };

  const displayName = getDisplayPlanName(plan?.level);

  const renderPricingInfo = () => {
    if (isFree) {
      return null;
    }
    if (isLifetimeRecommended) {
      return (
        <div className="flex flex-col gap-1">
          <div
            className={cn(
              "text-content_primary body_bold_s px-[6px] py-0.5 rounded bg-content_aura_lv3_primary text-center w-fit",
            )}
          >
            {I18n.t("Best Value")}
          </div>
          <div className="text-content_primary headline_regular_m">
            {I18n.t("one-time")}
          </div>
        </div>
      );
    }
    if (priceMeta.discountPercent > 0 && priceMeta.originalPrice) {
      return (
        <div className="flex flex-col gap-1">
          <div
            className={cn(
              "text-content_primary body_bold_s px-1.5 py-0.5 rounded bg-content_aura_lv3_primary text-center",
              {
                "bg-[#C42410] text-content_primary":
                  isActivity && isRecommended,
              },
            )}
          >
            {priceMeta.discountPercent.toFixed(0)}
            {I18n.t("% OFF")}
          </div>
          <div className="text-content_tertiary body_medium_s line-through">
            ${priceMeta.originalPrice.toFixed(2)}
            {priceMeta.unit}
          </div>
        </div>
      );
    }
    return null;
  };

  // 试用续订说明只对「该套餐有在售试用产品」的卡片（年卡）展示，口径与 PurchaseActions 的
  // isTrialPlan && !isMember 一致：父级传入的 isTrial 是账户维度"存在试用产品"，
  // 会把年度试用的文案错放到周/月卡上。
  const isTrialPlanCard =
    userPlanInfo?.isFree === true &&
    !!trialList?.some(
      (item) =>
        item.renewal_id === plan?.id && item.status === ProductStatus.ACTIVE,
    );

  const renderPurchaseDescription = () => {
    if (!isFree) {
      if (isLifetimeRecommended) {
        return (
          <span className="text-content_tertiary body_medium_s">
            {I18n.t("One-time payment · no renewal")}
          </span>
        );
      }
      if (isTrialPlanCard) {
        return (
          <>
            <p>
              {I18n.t("After 7 days, renews at $299.99/year unless cancelled.")}
            </p>
            <p>{I18n.t("Cancel anytime in Settings.")}</p>
          </>
        );
      }
    }
    return (
      <div
        className={billingCycle === BillingCycle.LIFETIME ? "h-4" : "h-8"}
      ></div>
    );
  };

  return (
    <div
      className={cn(
        "flex flex-col hover:translate-y-[-6px] transition-all ease-[cubic-bezier(0.66,0,0.34,1)] duration-600 h-[851px]",
        {
          "max-md:mt-9 !w-[400px] bg-content_accent4_primary p-1 rounded-3":
            isRecommended,
        },
        { "!w-[320px]": isFree },
      )}
    >
      {isRecommended ? (
        <div className={cn("h-9", { "h-[58px]": isActivity })}>
          {isActivity ? (
            <div
              className="px-4 py-2 rounded-t-3 rounded-b-0 w-full h-full flex items-center justify-between"
              style={{
                background:
                  "url(/assets/images/activity/chirstmas-icon.png) no-repeat left center #145134",
                backgroundSize: "58px 100%",
              }}
            >
              <div className="text-content_primary title_l ml-1">
                {I18n.t("Christmas Offer")}
              </div>
              <div className="flex items-center">
                <Countdown
                  countdown={time}
                  labelMap={{}}
                  className="md:w-8 md:h-8 rounded-2"
                  containerClassName="md:w-4"
                  textClassName="md:title_m md:w-4"
                  countDownClassName="md:w-8 md:h-8"
                />
              </div>
            </div>
          ) : (
            <div className="bg-content_accent4_primary border border-border_accent4_solid rounded-t-3 rounded-b-0 w-full text-content_inverse_primary title_l h-9 gap-1 py-2 flex flex-row items-center justify-center">
              <Icon
                name="best"
                size={16}
                color={ospThemes.colors.content_inverse_primary}
              />
              {isLifetimeRecommended
                ? I18n.t("Founding Pass · Limited")
                : I18n.t("Most Popular")}
            </div>
          )}
        </div>
      ) : (
        <div
          className={cn("h-10", {
            "h-[58px]": hasActiveDiscount && isDiscount,
          })}
        />
      )}
      <div
        className={cn(
          "rounded-3 p-6 gap-[10px] flex-1 max-w-full flex flex-col relative gradient-border-member-card-default bg-black/3 md:bg-white/3",
          {
            "no-gradient-border !bg-background_solid_neutral_secondary !rounded-2":
              isRecommended && !isActivity,
          },
          {
            "bg-[#082C1B] gradient-border-member-card-christmas rounded-t-[0px]":
              isActivity && isRecommended,
          },
        )}
      >
        <div className="flex flex-col gap-8">
          <div>
            <div className="flex flex-col gap-2">
              <h2
                aria-label="Subscription Card h2"
                data-openreplay-label="member_subscriptioncard_h2"
                id="sem-subscription-card-h2"
                className="text-content_primary headline_regular_l flex items-center gap-2"
              >
                {displayName}{" "}
                {isLifetimeRecommended ? (
                  <span className="text-content_tertiary body_medium_s">
                    {I18n.t("·Founding Pass")}
                  </span>
                ) : null}
              </h2>
              <div
                className={cn("text-content_secondary body_medium_l h-18", {
                  "text-content_primary": isActivity && isRecommended,
                })}
              >
                {getPlanNameDescription()}
              </div>
            </div>

            <div className="flex items-end gap-3 mt-10 mb-1">
              <div
                className={cn("flex items-baseline text-content_primary", {
                  "text-[#FFDD84]": isActivity && isRecommended,
                })}
              >
                <span className="display_s">$</span>
                <span className="display_s">
                  <AnimateNumber value={priceMeta.displayPrice} />
                </span>
                {priceMeta.unit ? (
                  <span className="headline_regular_m">{priceMeta.unit}</span>
                ) : null}
              </div>
              {renderPricingInfo()}
            </div>
            {priceMeta.chargeNote ? (
              <div className="text-content_tertiary body_medium_s">
                {priceMeta.chargeNote}
              </div>
            ) : (
              <div className="h-4"></div>
            )}

            <PurchaseActions
              isFree={isFree}
              isLoading={isLoading}
              userPlanInfo={userPlanInfo}
              billingCycle={billingCycle}
              plan={plan}
              isTrial={isTrial}
              onStripeClick={onStripeClick}
              onPaypalClick={onPaypalClick}
              trialList={trialList}
              disableInvisiblePlaceholder
              className="mt-10 mb-4"
            />
            <div className="text-content_tertiary body_medium_s text-center mt-4">
              {renderPurchaseDescription()}
            </div>
          </div>
          <div className="border-t border-border_white_transparent10"></div>
          <FeatureStatic
            isFree={isFree}
            isRecommended={isRecommended}
            billingCycle={billingCycle}
          />
        </div>
      </div>
    </div>
  );
};

export default memo(SubscriptionCard);
